import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Languages, Ship } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const { t, lang, setLang } = useLang();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/");
  };

  return (
    <div className="min-h-screen flex bg-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Left panel - visual */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 text-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20"
             style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #2563EB 0, transparent 40%), radial-gradient(circle at 80% 80%, #0EA5E9 0, transparent 40%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-blue-600 flex items-center justify-center font-bold text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>
              K
            </div>
            <div>
              <div className="font-bold text-lg tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>K-LOGIC</div>
              <div className="text-xs text-slate-400 -mt-0.5">KAM Logistics, Order, and General Inventory Control</div>
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("tagline")}
            </h1>
            <p className="text-slate-300 text-sm leading-relaxed max-w-md">
              Finish Goods · Master Carton · Plastic · Shipment · Purchasing · Priority Dispatch.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-4 max-w-md">
              {[
                { k: "01", v: "Multi-Role" },
                { k: "02", v: "Shortage Calc" },
                { k: "03", v: "Priority Queue" },
                { k: "04", v: "Excel Export" },
              ].map((x) => (
                <div key={x.k} className="border-l-2 border-blue-500 pl-3">
                  <div className="font-mono text-xs text-slate-400">{x.k}</div>
                  <div className="font-semibold text-sm">{x.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500 font-mono">© 2026 K-LOGIC · v1.0</div>
        </div>
      </div>

      {/* Right panel - form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-10">
            <Link to="/login" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-md bg-slate-900 flex items-center justify-center">
                <Ship size={18} className="text-white" />
              </div>
              <span className="font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>K-LOGIC</span>
            </Link>
            <button
              onClick={() => setLang(lang === "id" ? "en" : "id")}
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-slate-100 hover:bg-slate-200 rounded-md transition-colors text-slate-700"
              data-testid="language-toggle-button"
            >
              <Languages size={14} />
              <span className="uppercase">{lang}</span>
            </button>
          </div>

          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("welcome")}
          </h2>
          <p className="text-sm text-slate-500 mb-8">{t("sign_in")}</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-medium text-slate-500">{t("email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-medium text-slate-500">{t("password")}</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="mt-1.5 h-11"
              />
            </div>

            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error-text">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
              data-testid="login-submit-button"
            >
              {busy ? "..." : t("sign_in")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
